import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Autocomplete, Box, Button, Grid, InputAdornment, Stack, TextField, } from "@mui/material";
import { useBatchPaymentDetailsByBatchPaymentQuery, useBatchPaymentsByIdQuery, useFinanceSummaryQuery, useUpdateBatchPaymentMutationMutation, } from "generated/generated-models";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { Loading } from "app/lib/components/Loading";
import { CError } from "app/common/CError";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { MoneyFormatted } from "app/components/MoneyFormatted";
import NoRowsOverlay from "app/components/NoRowsOverlay";
import { Navigate, useLocation, useNavigate, useParams, } from "react-router-dom";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { toDateOnly } from "core/util/dateExtensions";
import { DelayedRender } from "app/lib/components/DelayedRender";
export const EditBatchPaymentsPage = () => {
    const params = useParams();
    const batchPaymentId = params.id ?? "";
    const { data: batchPaymentDetailsData, loading: batchPaymentDetailsLoading, error: batchPaymentDetailsError, } = useBatchPaymentDetailsByBatchPaymentQuery({
        variables: { input: { BatchPaymentId: batchPaymentId } },
        fetchPolicy: "network-only",
    });
    const { data, loading, error } = useFinanceSummaryQuery();
    const { data: batchPaymentData, loading: batchPaymentLoading, error: batchPaymentError, } = useBatchPaymentsByIdQuery({
        variables: { input: { BatchPaymentId: batchPaymentId } },
        fetchPolicy: "network-only",
    });
    if (loading || batchPaymentLoading || batchPaymentDetailsLoading) {
        return _jsx(Loading, {});
    }
    if (error || batchPaymentError || batchPaymentDetailsError) {
        return _jsx(CError, { error: "Could not retrieve Batch Payment Details" });
    }
    return (_jsx(EditBatchPayments, { data: data, batchPaymentData: batchPaymentData, batchPaymentDetailsData: batchPaymentDetailsData, batchPaymentId: batchPaymentId }));
};
const EditBatchPayments = ({ data, batchPaymentDetailsData, batchPaymentData, batchPaymentId, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const warningMessages = location.state;
    const batchPaymentDetails = batchPaymentDetailsData?.batchPaymentDetails?.BatchPaymentDetails;
    const organisationName = batchPaymentDetails?.find((payment) => payment?.RemitterName !== undefined)?.RemitterName ?? "";
    const [fromAccount, setFromAccount] = useState(batchPaymentData?.batchPaymentsById?.Account.Id);
    const [remitterName, setRemitterName] = useState(batchPaymentDetails?.find((payment) => payment?.RemitterName !== undefined)?.RemitterName ?? "");
    const [scheduledDate, setScheduledDate] = useState(dayjs(batchPaymentData?.batchPaymentsById?.ScheduledDate));
    const [description, setDescription] = useState(batchPaymentData?.batchPaymentsById?.Description ?? "");
    const [updateBatchPaymentMutationMutation, { loading: loadingMutation, error: errorMutation, data: dataMutation },] = useUpdateBatchPaymentMutationMutation({});
    const accountNumbers = data?.financeSummary.Instruments.flatMap((instrument) => instrument.Accounts).map((account) => String(account.Number)) ?? [];
    const columns = [
        {
            field: "Payment",
            headerName: "Payment",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.row.Id) + 1,
        },
        {
            field: "ToAccount",
            headerName: "To Account",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            renderCell: (params) => (_jsxs(Stack, { textAlign: "center", children: [_jsx(Box, { children: params.row.BankIdentifier.BankIdentifier }), _jsx(Box, { children: params.row.AccountNumber })] })),
        },
        {
            field: "AccountName",
            headerName: "Account Name",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "Indicator",
            headerName: "Indicator",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "TransactionCode",
            headerName: "Transaction Code",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "Amount",
            headerName: "Amount",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (_jsx(MoneyFormatted, { amount: params.row.Amount.Amount })),
        },
        {
            field: "LodgementReference",
            headerName: "Lodgement Reference ",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "WithholdingTax",
            headerName: "Withholding Tax",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (_jsx(MoneyFormatted, { amount: params.row.WithholdingTax.Amount })),
        },
    ];
    const handleSubmit = async () => {
        await updateBatchPaymentMutationMutation({
            variables: {
                input: {
                    AccountId: fromAccount,
                    BatchPayment: batchPaymentId,
                    RemitterName: remitterName,
                    ScheduledDate: toDateOnly(scheduledDate.toDate()),
                    Description: description,
                },
            },
        });
    };
    const handleAccountChange = (val) => {
        if (val === null) {
            return;
        }
        const accountId = data?.financeSummary.Instruments.flatMap((instrument) => instrument.Accounts).find((account) => account.Number === val)?.Id;
        setFromAccount(accountId);
    };
    return (_jsx(_Fragment, { children: _jsxs("section", { className: "sub-wrap", children: [_jsx("h2", { children: "Edit Batch Payments" }), _jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2, padding: 1, children: [_jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(Autocomplete, { id: "filtered-account-outlined", size: "small", options: accountNumbers, getOptionLabel: (option) => option ? option : "", defaultValue: batchPaymentData?.batchPaymentsById?.Account
                                            .Number, onChange: (_e, val) => handleAccountChange(val), renderInput: (params) => (_jsx(TextField, { ...params, label: "From Account", InputLabelProps: { shrink: true } })) }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "Organisation Name", InputLabelProps: { shrink: true }, defaultValue: organisationName, onChange: (e) => setRemitterName(e.target.value) }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(DatePicker, { slotProps: {
                                            textField: {
                                                size: "small",
                                                InputLabelProps: {
                                                    shrink: true,
                                                    focused: warningMessages?.includes("Scheduled Date"),
                                                },
                                                color: warningMessages?.includes("Scheduled Date")
                                                    ? "warning"
                                                    : "primary",
                                                sx: {
                                                    "& .MuiFormHelperText-root": {
                                                        color: "#f1a464",
                                                    },
                                                },
                                                helperText: warningMessages?.includes("Scheduled Date")
                                                    ? "Scheduled date is in the past. Please check date is correct."
                                                    : "",
                                            },
                                        }, label: "Scheduled Date", defaultValue: dayjs(batchPaymentData?.batchPaymentsById
                                            ?.ScheduledDate), onChange: (date) => setScheduledDate(date ? date : dayjs(new Date())) }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { size: "small", label: "Description", defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.Description, InputLabelProps: {
                                            shrink: true,
                                            focused: warningMessages?.includes("Description"),
                                        }, onChange: (e) => setDescription(e.target.value), color: warningMessages?.includes("Description")
                                            ? "warning"
                                            : "primary", helperText: warningMessages?.includes("Description")
                                            ? "A description is required. Please enter a description."
                                            : "", sx: {
                                            "& .MuiFormHelperText-root": {
                                                color: "#f1a464",
                                            },
                                        } }) })] }), _jsxs(Grid, { container: true, spacing: 2, padding: 1, children: [_jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { disabled: true, size: "small", defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.PaymentsCount, label: "Total Payments", InputLabelProps: { shrink: true } }) }), _jsx(Grid, { item: true, xs: 6, md: 3, children: _jsx(TextField, { disabled: true, size: "small", label: "Amount", InputLabelProps: { shrink: true }, inputProps: {
                                            amount: batchPaymentData?.batchPaymentsById
                                                ?.Total?.Amount,
                                        }, InputProps: {
                                            inputComponent: MoneyFormatted,
                                        }, defaultValue: batchPaymentData?.batchPaymentsById?.Total
                                            .Amount }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(TextField, { disabled: true, size: "small", label: "Status", InputLabelProps: {
                                            shrink: true,
                                            disabled: true,
                                        }, defaultValue: batchPaymentData?.batchPaymentsById?.Status.replace(/([A-Z])/g, " $1").trim() }) }), _jsx(Grid, { item: true, xs: 12, md: 3, children: _jsx(TextField, { disabled: true, defaultValue: batchPaymentData?.batchPaymentsById
                                            ?.AuthorisedByName, size: "small", label: "Authorised By", InputLabelProps: { shrink: true }, InputProps: {
                                            endAdornment: (_jsx(InputAdornment, { position: "end", children: batchPaymentData?.batchPaymentsById
                                                    ?.AuthorisedDate &&
                                                    dayjs(batchPaymentData
                                                        ?.batchPaymentsById
                                                        ?.AuthorisedDate).format("DD/MM/YYYY") })),
                                        } }) })] })] }), _jsx(Box, { padding: 1, children: _jsx(DataGridPro, { className: "custom-grid", columns: columns, rows: batchPaymentDetails ?? [], getRowId: (r) => r.Id, pageSizeOptions: [10, 25, 50], autoHeight: true, pagination: true, initialState: {
                            pagination: {
                                paginationModel: { pageSize: 10 },
                            },
                        }, slots: {
                            noRowsOverlay: () => (_jsx(NoRowsOverlay, { children: "There are currently no batch payment details for this batch payment." })),
                        }, disableColumnSelector: true }) }), _jsxs(Box, { display: "flex", alignItems: "center", height: "100%", flexWrap: "wrap", columnGap: 2, rowGap: 1, justifyContent: "flex-end", children: [errorMutation && (_jsx(CError, { error: "Could not edit batch payment details: " +
                                errorMutation.message })), !loadingMutation &&
                            dataMutation?.updateBatchPayment?.Success && (_jsx(Box, { width: "100%", children: _jsxs(Alert, { severity: "success", children: ["Batch Payment Details has been updated successfully", _jsx(DelayedRender, { delay: 1500, children: _jsx(Navigate, { to: "/transfersAndPayments/BatchPayments" }) })] }) })), _jsx(Button, { variant: "outlined", color: "secondary", onClick: () => navigate("/transfersAndPayments/BatchPayments"), children: "Cancel" }), _jsx(LoadingButton, { variant: "contained", color: "primary", type: "submit", loading: loadingMutation, onClick: () => handleSubmit(), children: "Save" })] })] }) }));
};
